// =============================================================================
//  BLOC 4 — LE PLAN DAL : des PROPOSITIONS, pas une sortie de LLM à approuver
// =============================================================================
//  🔴 CE QUI MANQUAIT : `preconisations` était un `List[str]` — une chaîne dans
//  une liste, sans identifiant. L'opérateur ne pouvait qu'approuver GLOBALEMENT
//  ce que l'IA avait écrit. Ici, chaque proposition est IDENTIFIÉE, et se
//  décide une par une : accepter · amender · écarter.
//
//  🔑 ET L'ÉCRAN MONTRE LA CADUCITÉ. Si le contenu a changé depuis la
//  validation, l'opérateur doit le VOIR ici — ⛔ pas le découvrir au refus de
//  génération. Une garde qui ne se lit qu'au moment du refus arrive trop tard.
//
//  ⚠️ Déclaré HORS de tout composant parent, et inséré par UNE ligne dans
//  `DALForm.jsx` : ce fichier fait 1700+ lignes et sert en exploitation.
// =============================================================================
import { useCallback, useEffect, useState } from "react";
import { Loader2, Check, Pencil, X, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ⚠️ Paires fond/texte dual-mode. ⛔ Jamais une couleur Tailwind en dur.
const ETAT_STYLE = {
  proposee: { bg: "var(--oh-stat-blue-bg)", text: "var(--oh-stat-blue-text)", label: "proposée" },
  acceptee: { bg: "var(--oh-stat-green-bg)", text: "var(--oh-stat-green-text)", label: "acceptée" },
  amendee: { bg: "var(--oh-stat-cyan-bg)", text: "var(--oh-stat-cyan-text)", label: "amendée" },
  ecartee: { bg: "var(--oh-stat-slate-bg)", text: "var(--oh-stat-slate-text)", label: "écartée" },
};

// 🔑 Les quatre états du PLAN, et ce qu'ils veulent dire pour l'opérateur.
const VALIDATION_STYLE = {
  regime_anterieur: { bg: "var(--oh-stat-slate-bg)", text: "var(--oh-stat-slate-text)", titre: "Régime antérieur" },
  absente: { bg: "var(--oh-stat-amber-bg)", text: "var(--oh-stat-amber-text)", titre: "Plan proposé — non validé" },
  caduque: { bg: "var(--oh-stat-red-bg)", text: "var(--oh-stat-red-text)", titre: "Validation CADUQUE" },
  valide: { bg: "var(--oh-stat-green-bg)", text: "var(--oh-stat-green-text)", titre: "Plan validé" },
};

function quand(iso) {
  if (!iso) return "";
  return String(iso).slice(0, 16).replace("T", " à ");
}

export default function PlanDalPanel({ dossierId, tenantId, onPlanChange }) {
  const [plan, setPlan] = useState(null);
  const [resume, setResume] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(null);
  const [amendementDe, setAmendementDe] = useState(null);
  const [texteAmende, setTexteAmende] = useState("");
  const [erreur, setErreur] = useState(null);

  const entetes = useCallback(() => {
    const h = { "Content-Type": "application/json" };
    if (tenantId) h["X-Tenant-ID"] = tenantId;
    return h;
  }, [tenantId]);

  const charger = useCallback(async () => {
    if (!dossierId) return;
    setChargement(true);
    try {
      const r = await fetch(`${API}/api/dal/dossiers/${dossierId}/plan`,
        { headers: entetes(), credentials: "include" });
      if (!r.ok) { setPlan(null); setResume(null); return; }
      const d = await r.json();
      setPlan(d.plan || null);
      setResume(d.resume || null);
    } catch (e) {
      setErreur(`Chargement du plan impossible : ${e.message}`);
    } finally {
      setChargement(false);
    }
  }, [dossierId, entetes]);

  useEffect(() => { charger(); }, [charger]);

  const decider = useCallback(async (proposition, etat, texte) => {
    setEnCours(proposition.id);
    setErreur(null);
    try {
      const r = await fetch(
        `${API}/api/dal/dossiers/${dossierId}/plan/proposition/${proposition.id}/decision`,
        { method: "POST", headers: entetes(), credentials: "include",
          body: JSON.stringify({ etat, texte }) });
      const d = await r.json();
      if (!r.ok) { setErreur(d.detail || `Erreur HTTP ${r.status}`); return; }
      setAmendementDe(null);
      setTexteAmende("");
      await charger();
      if (onPlanChange) onPlanChange();
    } catch (e) {
      setErreur(`Décision impossible : ${e.message}`);
    } finally {
      setEnCours(null);
    }
  }, [dossierId, entetes, charger, onPlanChange]);

  const valider = useCallback(async () => {
    setEnCours("__valider__");
    setErreur(null);
    try {
      const r = await fetch(`${API}/api/dal/dossiers/${dossierId}/plan/valider`,
        { method: "POST", headers: entetes(), credentials: "include" });
      const d = await r.json();
      if (!r.ok) {
        // ⚠️ Le motif EXACT du refus, ⛔ pas « erreur » : l'opérateur doit
        // savoir CE QUI manque, pas qu'il manque quelque chose.
        setErreur(d.detail?.motif || d.detail || `Erreur HTTP ${r.status}`);
        return;
      }
      await charger();
      if (onPlanChange) onPlanChange();
    } catch (e) {
      setErreur(`Validation impossible : ${e.message}`);
    } finally {
      setEnCours(null);
    }
  }, [dossierId, entetes, charger, onPlanChange]);

  if (chargement) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm"
           style={{ color: "var(--oh-text-secondary)" }} data-testid="plan-dal-chargement">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement du plan…
      </div>
    );
  }

  const etatValidation = resume?.etat_validation || "regime_anterieur";
  const style = VALIDATION_STYLE[etatValidation] || VALIDATION_STYLE.absente;
  const propositions = plan?.propositions || [];

  // ⚠️ RÉGIME ANTÉRIEUR : on le DIT, ⛔ on ne masque pas le panneau. Un écran
  // vide laisserait croire à un bug ; ici l'opérateur sait où il en est.
  if (!propositions.length) {
    return (
      <div className="rounded-xl p-4" data-testid="plan-dal-panel"
           style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)" }}>
        <div className="flex items-center gap-2">
          <span style={{ color: "var(--oh-text)", fontWeight: 500 }}>Plan DAL</span>
          <span className="px-2 py-0.5 rounded text-xs" data-testid="plan-dal-etat"
                style={{ background: style.bg, color: style.text }}>
            {style.titre}
          </span>
        </div>
        <p className="mt-2 text-sm" style={{ color: "var(--oh-text-secondary)" }}>
          Aucun plan proposé pour ce dossier. La génération du DAL suit le
          comportement habituel, sans contrôle de validation.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-4" data-testid="plan-dal-panel"
         style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)" }}>

      {/* ═══ LE BANDEAU : il dit CE QUI est validé, ⛔ pas seulement « validé » ═══ */}
      <div className="rounded-lg p-3" data-testid="plan-dal-bandeau"
           style={{ background: style.bg, color: style.text }}>
        <div className="flex flex-wrap items-center gap-2">
          {etatValidation === "valide" ? <ShieldCheck className="w-4 h-4" />
            : etatValidation === "caduque" ? <AlertTriangle className="w-4 h-4" /> : null}
          <span style={{ fontWeight: 500 }} data-testid="plan-dal-etat">{style.titre}</span>
          <span className="text-xs">
            {resume.par_etat.acceptee} acceptée(s) · {resume.par_etat.amendee} amendée(s)
            {" "}· {resume.par_etat.ecartee} écartée(s) · {resume.par_etat.proposee} en attente
          </span>
        </div>
        <p className="mt-1 text-sm">{resume.motif}</p>

        {/* 🔑 L'EMPREINTE, MONTRÉE. C'est elle qui lie la validation au contenu :
            si elle diffère, l'opérateur le voit ICI, ⛔ pas au refus de génération. */}
        {resume.validation?.empreinte && (
          <div className="mt-2 text-xs" style={{ fontFamily: "monospace" }}
               data-testid="plan-dal-empreintes">
            <div>validée&nbsp;: {resume.validation.empreinte.slice(0, 16)}…</div>
            <div>actuelle&nbsp;: {(resume.empreinte_actuelle || "").slice(0, 16)}…</div>
            {etatValidation === "caduque" && (
              <div className="mt-1" style={{ fontFamily: "inherit", fontWeight: 500 }}>
                ⛔ Les deux diffèrent : le plan a été modifié après validation.
                La génération refusera tant qu'il n'est pas revalidé.
              </div>
            )}
          </div>
        )}
      </div>

      {erreur && (
        <div className="mt-3 rounded-lg p-2 text-sm" data-testid="plan-dal-erreur"
             style={{ background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }}>
          {typeof erreur === "string" ? erreur : JSON.stringify(erreur)}
        </div>
      )}

      {/* ═══ LES PROPOSITIONS, UNE PAR UNE ═══════════════════════════════ */}
      <ul className="mt-3 space-y-2">
        {propositions.map((p) => {
          const st = ETAT_STYLE[p.etat] || ETAT_STYLE.proposee;
          const occupe = enCours === p.id;
          return (
            <li key={p.id} className="rounded-lg p-3" data-testid={`plan-dal-proposition-${p.id}`}
                style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)" }}>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {/* ⚠️ L'IDENTIFIANT est AFFICHÉ : c'est ce qui prouve qu'une
                    proposition est un objet désignable, ⛔ plus une chaîne. */}
                <code style={{ color: "var(--oh-text-muted)" }}>{p.id}</code>
                <span style={{ color: "var(--oh-text-secondary)" }}>{p.zone}</span>
                <span className="px-2 py-0.5 rounded"
                      style={{ background: st.bg, color: st.text }}
                      data-testid={`plan-dal-etat-${p.id}`}>
                  {st.label}
                </span>
                <span style={{ color: "var(--oh-text-muted)" }}>origine&nbsp;: {p.origine}</span>
                {p.decide_par && (
                  <span style={{ color: "var(--oh-text-muted)" }}
                        data-testid={`plan-dal-trace-${p.id}`}>
                    {/* ⚠️ QUI et QUAND, sur chaque geste. */}
                    décidé par {p.decide_par} le {quand(p.decide_le)}
                  </span>
                )}
              </div>

              <p className="mt-2 text-sm" style={{ color: "var(--oh-text)" }}
                 data-testid={`plan-dal-texte-${p.id}`}>
                {p.texte}
              </p>

              {/* 🔑 Le texte d'ORIGINE reste lisible après amendement : sans lui,
                  on ne saurait plus ce que l'IA avait proposé. */}
              {p.etat === "amendee" && p.texte_origine !== p.texte && (
                <p className="mt-1 text-xs" style={{ color: "var(--oh-text-muted)" }}
                   data-testid={`plan-dal-origine-${p.id}`}>
                  proposition initiale de l'IA&nbsp;: « {p.texte_origine} »
                </p>
              )}

              {p.justification && (
                <p className="mt-1 text-xs italic" style={{ color: "var(--oh-text-secondary)" }}>
                  {p.justification}
                </p>
              )}

              {amendementDe === p.id ? (
                <div className="mt-2">
                  <textarea
                    value={texteAmende}
                    onChange={(e) => setTexteAmende(e.target.value)}
                    rows={3}
                    placeholder="Rédigez la préconisation telle qu'elle figurera dans le DAL"
                    data-testid={`plan-dal-champ-amendement-${p.id}`}
                    className="w-full rounded-lg p-2 text-sm"
                    style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)",
                             border: "0.5px solid var(--oh-border)" }}
                  />
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" disabled={occupe || !texteAmende.trim()}
                            data-testid={`plan-dal-confirmer-amendement-${p.id}`}
                            onClick={() => decider(p, "amendee", texteAmende)}
                            style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
                      Enregistrer l'amendement
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => { setAmendementDe(null); setTexteAmende(""); }}
                            style={{ border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)" }}>
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" disabled={occupe} data-testid={`plan-dal-accepter-${p.id}`}
                          onClick={() => decider(p, "acceptee")}
                          style={{ background: "transparent", border: "1px solid var(--oh-accent)",
                                   color: "var(--oh-accent-text)" }}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Accepter
                  </Button>
                  <Button size="sm" disabled={occupe} data-testid={`plan-dal-amender-${p.id}`}
                          onClick={() => { setAmendementDe(p.id); setTexteAmende(p.texte); }}
                          style={{ background: "transparent", border: "1px solid var(--oh-border)",
                                   color: "var(--oh-text-secondary)" }}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Amender
                  </Button>
                  <Button size="sm" disabled={occupe} data-testid={`plan-dal-ecarter-${p.id}`}
                          onClick={() => decider(p, "ecartee")}
                          style={{ background: "transparent", border: "1px solid var(--oh-border)",
                                   color: "var(--oh-text-secondary)" }}>
                    <X className="w-3.5 h-3.5 mr-1" /> Écarter
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* ═══ LA VALIDATION HUMAINE, EXPLICITE ════════════════════════════ */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button disabled={enCours === "__valider__" || resume.par_etat.proposee > 0}
                onClick={valider} data-testid="plan-dal-valider"
                style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>
          {enCours === "__valider__" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {etatValidation === "caduque" ? "Revalider le plan" : "Valider le plan"}
        </Button>
        {resume.par_etat.proposee > 0 && (
          // ⚠️ On DIT pourquoi c'est indisponible. ⛔ Un bouton grisé muet
          // laisse l'opérateur deviner — et ce n'est jamais une garde, la
          // vraie garde est côté serveur.
          <span className="text-sm" style={{ color: "var(--oh-text-secondary)" }}
                data-testid="plan-dal-blocage">
            {resume.par_etat.proposee} proposition(s) restent à décider.
          </span>
        )}
      </div>
    </div>
  );
}
